/**
 * @file Provides fetch functions.
 */
import { setDefaultOptions } from './defaults'
import FetchError from './fetch_error'
import { prepareFetchArguments, prepareJsonFetchArguments } from './prepare_fetch_arguments'
import type { FetchOptionQueryHash, FetchOptions, FetchResource, OriginalFetchOptions } from './types'
import { HTTPAuthenticationScheme, HTTPContentType, HTTPMethod } from './types'

async function originalFetch(url: string, options: OriginalFetchOptions = {}): Promise<Response> {
  return fetch(url, options)
}

type DeadlineFetchResult = {
  response: Response
  done: () => void
  timedOut: () => boolean
  timeoutMs: number | undefined
}

async function fetchResponseWithDeadline(url: string, options: FetchOptions[]): Promise<DeadlineFetchResult> {
  const [fetchUrl, fetchOptions] = prepareFetchArguments(url, options)
  const timeoutMs = fetchOptions.timeoutMs
  delete fetchOptions.timeoutMs

  let timeoutId: ReturnType<typeof setTimeout> | undefined
  let timedOut = false
  const callerSignal = fetchOptions.signal
  let abortFromCaller: (() => void) | undefined

  function done(): void {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }
    if (callerSignal && abortFromCaller) {
      callerSignal.removeEventListener('abort', abortFromCaller)
      abortFromCaller = undefined
    }
  }

  if (typeof timeoutMs === 'number' && timeoutMs > 0) {
    const timeoutController = new AbortController()
    timeoutId = setTimeout(() => {
      timedOut = true
      timeoutController.abort()
    }, timeoutMs)

    if (callerSignal) {
      abortFromCaller = () => {
        timeoutController.abort()
      }
      if (callerSignal.aborted) {
        abortFromCaller()
      } else {
        callerSignal.addEventListener('abort', abortFromCaller, { once: true })
      }
    }

    fetchOptions.signal = timeoutController.signal
  }

  let response: Response
  try {
    response = await originalFetch(fetchUrl, fetchOptions)
    if (response && response.status >= 100 && response.status <= 399) {
      return {
        response,
        done,
        timedOut: () => timedOut,
        timeoutMs,
      }
    }
  } catch (error) {
    done()
    const e = error as Error
    if (timedOut) {
      throw new FetchError(0, `Request timed out after ${timeoutMs} ms`, undefined, error)
    }
    throw new FetchError(0, e.message, undefined, error)
  }

  let body: string
  try {
    body = await response.text()
  } catch (error) {
    done()
    if (timedOut) {
      throw new FetchError(
        0,
        `Request timed out after ${timeoutMs} ms while reading the response body`,
        undefined,
        error,
      )
    }
    // A mid-body drop rejects the read with a bare TypeError, which carries no status to branch on.
    const e = error as Error
    throw new FetchError(0, e.message, undefined, error)
  }
  done()
  throw new FetchError(response.status, body, response)
}

async function fetchResponse(url: string, ...options: FetchOptions[]): Promise<Response> {
  const result = await fetchResponseWithDeadline(url, options)
  result.done()
  return result.response
}

async function fetchJson<T>(url: string, ...options: FetchOptions[]): Promise<T> {
  const { response, done, timedOut, timeoutMs } = await fetchResponseWithDeadline(
    url,
    prepareJsonFetchArguments(options),
  )
  try {
    // A challenge page or captive portal answers a JSON request with HTML and a 2xx. Without this
    // guard `response.json()` throws a bare SyntaxError whose message is the parser's, not ours,
    // and the caller cannot tell it apart from a malformed API response.
    const contentType = response.headers?.get?.('content-type') ?? ''
    if (contentType.toLowerCase().includes('html')) {
      const body = await response.text()
      throw new FetchError(
        response.status,
        `Expected JSON, got ${contentType.split(';')[0].trim() || 'no content-type'}`,
        response,
        undefined,
        body,
      )
    }
    return await response.json()
  } catch (error) {
    if (timedOut()) {
      throw new FetchError(
        0,
        `Request timed out after ${timeoutMs} ms while reading the response body`,
        undefined,
        error,
      )
    }
    if (error instanceof FetchError || error instanceof SyntaxError) {
      throw error
    }
    // Any other body-read failure; see the wrap in fetchResponseWithDeadline.
    const e = error as Error
    throw new FetchError(0, e.message, undefined, error)
  } finally {
    done()
  }
}

async function fetchText(url: string, ...options: FetchOptions[]): Promise<string> {
  const { response, done, timedOut, timeoutMs } = await fetchResponseWithDeadline(url, options)
  try {
    return await response.text()
  } catch (error) {
    if (timedOut()) {
      throw new FetchError(
        0,
        `Request timed out after ${timeoutMs} ms while reading the response body`,
        undefined,
        error,
      )
    }
    // Any other body-read failure; see the wrap in fetchResponseWithDeadline.
    const e = error as Error
    throw new FetchError(0, e.message, undefined, error)
  } finally {
    done()
  }
}

async function fetchBlob(url: string, ...options: FetchOptions[]): Promise<Blob> {
  const result = await fetchResponseWithDeadline(url, options)
  result.done()
  return result.response.blob()
}

function resource<T>(baseUrlWithoutTrailingSlash: string, ...baseOptions: FetchOptions[]): FetchResource<T> {
  function localFetch<Q>(relativePath: string, ...options: FetchOptions[]): Promise<Q> {
    const fullUrl =
      relativePath === '' ? baseUrlWithoutTrailingSlash : [baseUrlWithoutTrailingSlash, relativePath].join('/')
    return fetchJson<Q>(fullUrl, ...baseOptions, ...options)
  }
  const fetchResource: FetchResource<T> = {
    index(query: FetchOptionQueryHash, ...options: FetchOptions[]): Promise<T[]> {
      return localFetch<T[]>('', ...options, { method: HTTPMethod.get, query })
    },
    show(id: string | number, ...options: FetchOptions[]): Promise<T> {
      return localFetch(id.toString(), ...options, { method: HTTPMethod.get })
    },
    create(jsonData: any, ...options: FetchOptions[]): Promise<T> {
      return localFetch('', ...options, { method: HTTPMethod.post, jsonData })
    },
    update(jsonData: any, ...options: FetchOptions[]): Promise<T> {
      return localFetch(jsonData.id.toString(), ...options, { method: HTTPMethod.put, jsonData })
    },
    destroy(id: string | number, ...options: FetchOptions[]): Promise<T> {
      return localFetch(id.toString(), ...options, { method: HTTPMethod.delete })
    },

    get(relativePath: string, ...options: FetchOptions[]): Promise<unknown> {
      return localFetch<unknown>(relativePath, ...options, { method: HTTPMethod.get })
    },
    post(relativePath: string, jsonData: any, ...options: FetchOptions[]): Promise<unknown> {
      return localFetch<unknown>(relativePath, ...options, { method: HTTPMethod.post, jsonData })
    },
    put(relativePath: string, jsonData: any, ...options: FetchOptions[]): Promise<unknown> {
      return localFetch<unknown>(relativePath, ...options, { method: HTTPMethod.put, jsonData })
    },
    delete(relativePath: string, ...options: FetchOptions[]): Promise<unknown> {
      return localFetch<unknown>(relativePath, ...options, { method: HTTPMethod.delete })
    },

    fetch(relativePath: string, ...options: FetchOptions[]): Promise<unknown> {
      return localFetch<unknown>(relativePath, ...options)
    },
  }
  return fetchResource
}

export {
  FetchError,
  HTTPContentType,
  HTTPAuthenticationScheme,
  HTTPMethod,
  FetchOptions,
  FetchResource,
  originalFetch,
  fetchResponse,
  fetchJson,
  fetchText,
  fetchBlob,
  resource,
  setDefaultOptions as configure,
}
